<template>
   <!-- Loading -->
  <LoadingDiv v-if="isLoading" />

  <!-- Display -->
  <template v-else>
    <!-- Search -->
    <nav class="white benefit-search-bar" v-if="benefits?.length || search">
      <div class="nav-wrapper">
        <form method="POST" action="#" @submit.prevent>
          <div class="input-field blue-text text-darken-2 benefit-search-field">
            <input id="benefitSearch" type="search" v-model="searchItem" @focus="handleClearSearch()"
              placeholder="Search Benefits by Item or Description" />
            <label class="label-icon benefit-search-icon" for="benefitSearch" aria-hidden="true">
              <i class="material-icons">search</i>
            </label>
            <button type="button" class="benefit-search-clear" @click="handleClearSearch()" v-if="searchItem"
              aria-label="Clear benefits search">
              <i class="material-icons">close</i>
            </button>
          </div>
        </form>
      </div>
    </nav>
    <div class="progress down" v-if="isSearching">
      <div class="indeterminate"></div>
    </div>


    <div class="card z-depth-0 benefit-list" v-if="benefits?.length">
      <div class="card-panel z-depth-0 benefit-row" v-for="benefit in benefits" :key="benefit.id">
        <div class="row">
          <div class="col s12">
            <h6 class="benefit-row-content">
              {{ benefit.item }}
              <span class="secondary-content chip benefit-description">{{
                benefit.description
              }}</span>
            </h6>
          </div>
        </div>
      </div>
      <br />
    </div>

    <div class="section center" v-if="!benefits?.length">
      <div class="card z-depth-0">
            <h6 class="chip yellow">No Benefits <em>found</em>...</h6>
      </div>
    </div>

    <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
  </template>
</template>

<script>
import { ref, onMounted, computed, watchEffect } from "vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import getItemsOfItemsOfItems from "@/composables/getItemsOfItemsOfItems";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "ShowBenefitComponent",
  components: { PaginationDiv, LoadingDiv },
  props: ["pid", "cid"],
  setup(props) {
    const isLoading = ref(true);
    const isSearching = ref(false);
    const response = ref(null);
    const error = ref(null);
    const search = ref(null);
    const searchItem = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);

    // get function
    const getItemsFromApi = (table, id1, id2) => {
      (async () => {
        const { res, err } = await getItemsOfItemsOfItems(table, id1, id2);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // end loading
        isLoading.value = false;
        // end searching
        isSearching.value = false;
      })();
    };

    // starter
    onMounted(() => {
      // get encounter
      getEncounter();
    });

    // get encounters
    const getEncounter = () => {
      // set current Url
      currUrl.value = "benefits/plan/search";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value, props.pid, props.cid);
    }

    // restart
    const handleClearSearch = () => {
      if (searchItem.value) {
        // start searching
        isSearching.value = true;
        // null search
        searchItem.value = null;
        // get encounters
        getEncounter();
      }
    };

    // switch page
    const handlePage = (page) => {
      // start loading
      isLoading.value = true;
      // get new page items from function
      getItemsFromApi(currUrl.value, props.pid, `${props.cid}${currHook.value}page=${page}`);
      // move up
      // context.emit("moveUp", true);
    };

    // searcher
    const searcher = (val) => {
      // wake search
      search.value = true;
      // start searching
      isSearching.value = true;
      // set current Url
      currUrl.value = "benefits/plan/search";
      // set future Hook
      currHook.value = `?q=${val}&`;
      // search
      getItemsFromApi(currUrl.value, props.pid, `${props.cid}${currHook.value}`);
    }

    // search watcher
    watchEffect(() => {
      if (searchItem.value) {
        // search
        searcher(searchItem.value);
      } else {
        // get encounters
        getEncounter();
      }
    });

    // output results
    const benefits = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      isLoading,
      isSearching,
      handleClearSearch,
      handlePage,
      search,
      searchItem,
      currUrl,
      currHook,
      error,
      benefits,
      allPages,
    };
  },
};
</script>

<style scoped>
.benefit-search-bar {
  height: auto;
  min-height: 0;
}

.benefit-search-bar .nav-wrapper,
.benefit-search-bar form,
.benefit-search-field {
  height: 44px;
  min-height: 0;
}

.benefit-search-field {
  margin: 0;
  position: relative;
}

.benefit-search-field input[type="search"] {
  box-sizing: border-box;
  height: 44px;
  margin: 0;
  padding: 0 3rem !important;
}

.benefit-search-icon,
.benefit-search-clear {
  align-items: center;
  background: transparent;
  border: 0;
  color: #607d8b;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 44px;
  z-index: 2;
}

.benefit-search-icon {
  left: 0;
  pointer-events: none;
  transform: none;
}

.benefit-search-clear {
  cursor: pointer;
  right: 0;
}

.benefit-search-icon i,
.benefit-search-clear i {
  color: inherit;
  font-size: 1.15rem;
  line-height: 1;
}

.benefit-list {
  margin: 0 !important;
  padding: 0 !important;
}

.benefit-row {
  min-height: 0 !important;
  padding: 0.75rem 1rem !important;
}

.benefit-row .row {
  margin: 0 !important;
}

.benefit-row .col {
  padding: 0 !important;
}

.benefit-row-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.9rem;
  font-weight: 650;
  gap: 0.45rem;
  line-height: 1.35;
  margin: 0;
}

.benefit-description {
  float: none !important;
  font-size: 0.72rem;
  height: auto;
  line-height: 1.25;
  margin: 0;
  padding: 0.2rem 0.5rem;
}

.progress {
  margin-top: -15px;
}
.down {
  margin-top: 0px;
}
</style>
